import { Text } from "@imminently/react/ui/core/text";
import Stack, { StackProps } from "@mui/material/Stack";
import { Fragment, ReactNode } from "react";

import IconButton from "@mui/material/IconButton";
import { Avatar } from "@/components/Avatar";
import TooltipText from "@/components/TooltipText";
import { Icon } from "@/components/Icon";

import {
  formatName,
  getDOB,
  getName,
  getPatientEncounterPhoto,
  getSex,
  makeDOBString,
} from "@/global";
import { DELEGATE_INFO } from "@/resources/constants";
import { PatientEncounter } from "@/resources/types";
import { Theme, css } from "@/theme";
import { useGenericAddPatientModal } from "@/modals/genericForm";
import { useAddExpectsPatientModal } from "@/modals/addExpectsPatient";
import { useConciergeMode } from "@/contexts/ConciergeModeContext";
import PatientName from "./PatientName";
import { useSettingsContext } from "@/pages/settings/SettingsProvider";
import { findOptionFromSettings } from "@/util/MiscUtils";
import { ExternalRegisterDot } from "@/components/externalregister/ExternalRegisterDot";
import {
  getPatientExternalRegisterAlerts,
  type PatientWithExternalRegisterSummary,
} from "@/components/externalregister/patientSummary";
import { canRenderExternalRegisterDotIndicator } from "@/util/externalRegister";

const styles = {
  root: (theme: Theme) => css`
    padding: 1rem;
    background-color: ${theme.palette.grey[50]};
  `,
  avatar: (theme: Theme) => css`
    width: 5.5rem;
    height: 5.5rem;
    background-color: ${theme.palette.primary[100]};
    > span {
      font-size: 1.25rem;
      line-height: 1.2;
      font-weight: 700;
      font-family: ${theme.typography.h5.fontFamily};
    }
  `,
  details: (theme: Theme) => css`
    color: ${theme.palette.primary[500]};
  `,
};

export type PatientHeaderProps = {
  encounter: PatientEncounter;
};

const DOT = "•";

/** A component that inserts DOT as a Text component between each node. */
const DotSeparatedNodes = ({
  nodes,
  ...props
}: { nodes: ReactNode[] } & StackProps) => {
  const children = nodes.map((node, index) => (
    <Fragment key={index}>
      {node}
      {index < nodes.length - 1 && <Text variant="caption">{DOT}</Text>}
    </Fragment>
  ));
  return <Stack {...props}>{children}</Stack>;
};

const NameText = ({ encounter }: { encounter: PatientEncounter }) => {
  const nameInfo = getName(encounter);
  const name = nameInfo.fromPatient || nameInfo.fromDelegate || "-";

  const warning = !nameInfo.fromPatient && !!nameInfo.fromDelegate;
  const tooltipText =
    nameInfo.fromDelegate &&
    (nameInfo.fromPatient
      ? `${DELEGATE_INFO.DIFFERS} ${nameInfo.fromDelegate}`
      : DELEGATE_INFO.PROVIDED);

  return (
    <TooltipText
      text={name}
      variant="h2"
      warning={warning}
      sx={{ maxWidth: "fit-content" }}
    >
      {tooltipText}
    </TooltipText>
  );
};

const SexGenderText = ({ encounter, type }: { encounter: PatientEncounter, type: "sex" | "gender" }) => {
  const info = {
    fromPatient: encounter.patient?.[type],
    fromDelegate: encounter.delegatedData?.[type],
  }
  const { settings } = useSettingsContext();
  const code = info.fromPatient || info.fromDelegate || "N/A";
  const text = findOptionFromSettings(settings.patientDetails[type + "Options"], code);
  const warning = !info.fromPatient && !!info.fromDelegate;
  // To render multiple lines in a tooltip when \n and <br/> don't work,
  // pass an array of React nodes, each representing a line, to TooltipText's children
  const tooltipLines = [
    <p key="label">{type.charAt(0).toUpperCase() + type.slice(1)}</p>,
    info.fromDelegate && (
      <span key="info">
        {info.fromPatient
          ? `${DELEGATE_INFO.DIFFERS} ${findOptionFromSettings(settings.patientDetails[type + "Options"], info.fromDelegate)}`
          : DELEGATE_INFO.PROVIDED}
      </span>
    ),
  ].filter(Boolean);

  return (
    <TooltipText text={text} warning={warning}>
      {tooltipLines}
    </TooltipText>
  );
};

const DOBText = ({ encounter }: { encounter: PatientEncounter }) => {
  const dobInfo = getDOB(encounter);
  const dob = dobInfo.fromPatient || dobInfo.fromDelegate;

  const text = dob ? makeDOBString(dob) : "N/A";
  const warning = !dobInfo.fromPatient && !!dobInfo.fromDelegate;
  const tooltipText =
    dobInfo.fromDelegate &&
    (dobInfo.fromPatient
      ? `${DELEGATE_INFO.DIFFERS} ${makeDOBString(dobInfo.fromDelegate)}`
      : DELEGATE_INFO.PROVIDED);

  return (
    <TooltipText text={text} warning={warning}>
      {tooltipText}
    </TooltipText>
  );
};

export const PatientHeader = ({ encounter }: PatientHeaderProps) => {
  const { patient } = encounter;
  const photo = getPatientEncounterPhoto(encounter) || undefined;
  const openEditPatientModal = useGenericAddPatientModal();
  const openEditExpectsPatientModal = useAddExpectsPatientModal();
  const { isConciergeMode } = useConciergeMode();
  const { settings } = useSettingsContext();
  if (!patient) {
    return (
      <Stack direction="row" gap={3} alignItems="center">
        <Avatar css={styles.avatar} name="Error" alt="Error" image={photo} />
        <Stack height="100%" justifyContent="space-evenly">
          <Text variant="h2" bold>
            Error
          </Text>
        </Stack>
      </Stack>
    );
  }

  const { firstName, lastName, umrn, pronouns } = patient;
  const name = [firstName, lastName].join(" ");
  const displayName = formatName(firstName, lastName);

  const pronounsText = findOptionFromSettings(settings.patientDetails.pronounsOptions, pronouns);
  const showExternalRegisterDot = canRenderExternalRegisterDotIndicator({
    isConciergeMode,
    settings,
  });
  const externalRegisterAlerts = showExternalRegisterDot
    ? getPatientExternalRegisterAlerts(patient as PatientWithExternalRegisterSummary)
    : [];

  const details = [
    <SexGenderText key="sex" encounter={encounter} type="sex" />,
    <SexGenderText key="gender" encounter={encounter} type="gender" />,
    <DOBText key="dob" encounter={encounter} />,
    umrn,
    pronounsText,
    externalRegisterAlerts.length > 0
      ? <ExternalRegisterDot key="external-register" alerts={externalRegisterAlerts} label="Additional notification present" />
      : null,
  ].filter(Boolean);

  const isExpects = encounter.status === "expected";

  const handleEditClick = () => {
    if (isExpects) {
      openEditExpectsPatientModal({ mode: "edit", encounter });
    } else {
      openEditPatientModal({
        mode: "edit",
        encounter,
        configId: isConciergeMode ? "concierge" : undefined
      });
    }
  };

  return (
    <Stack direction="row" gap={3} alignItems="center">
      <Avatar css={styles.avatar} name={name} alt={displayName} image={photo} />
      <Stack height="100%" justifyContent="space-evenly">
        <Stack display="flex" direction="row" alignItems="center" justifyContent="flex-start" alignContent="center" gap={1}>
          <PatientName encounter={encounter} variant="h2"
          />
          <IconButton
            onClick={handleEditClick}
          // size="small"
          >
            <Icon icon="edit" />
          </IconButton>
        </Stack>
        <DotSeparatedNodes
          css={styles.details}
          nodes={details}
          direction="row"
          alignItems="center"
          gap={2}
        />
      </Stack>
    </Stack>
  );
};
